import React, { useEffect, useState } from "react";
import { CaseLink } from "@/components/CaseLink";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Preparing, isPreparing } from "@/components/Preparing";
import { ActionNeeded } from "@/components/ActionNeeded";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StageChip } from "@/components/StageChip";
import { formatINR, fmtDate, fmtLastConv, plural } from "@/lib/format";
import { Loader2, Sun, AlertTriangle, CalendarClock, Megaphone, CheckSquare, MessageCircle, Clock, Link2, Sparkles, Star, ChevronDown, ChevronRight } from "lucide-react";
import { NpsTaskRow } from "@/components/Nps";
import { CheckinCard } from "@/components/CheckinCard";
import { toast } from "sonner";
import { Info } from "@/components/Info";
import { StuckList } from "@/components/Tat";
import { Loading } from "@/components/Loading";
import { NewLeadButton } from "@/components/NewLead";
import { LeakRowActions } from "@/components/FollowupPicker";
import { loadMeta } from "@/lib/meta";
import { FIELDS } from "@/lib/fields";
import { GmailBanner, EmailToAssign } from "@/components/EmailInbox";
import { MyConsultStrip } from "@/components/MyConsultStrip";

export default function MyDay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wa, setWa] = useState({ nudges: [], catchup: [] });

  const load = async () => {
    setLoading(true);
    // P0: all My Day sections load in parallel; the WhatsApp ones never block the main page.
    const waP = Promise.all([api.get("/whatsapp/nudges"), api.get("/whatsapp/catchup")])
      .then(([n, cu]) => setWa({ nudges: n.data.nudges || [], catchup: cu.data.cases || [] }))
      .catch(() => {});
    try {
      const d = (await api.get("/my-day")).data;
      setData(d);
      if (d?._refreshing) setTimeout(() => api.get("/my-day").then((r) => { if (!r.data?._refreshing) setData(r.data); }).catch(() => {}), 6000);   // F56: pick up the rebuilt snapshot
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load My Day"); }
    finally { setLoading(false); }
    await waP;
  };
  useEffect(() => { load(); }, []);

  const confirmConfidence = async (row) => {      // F55: clear "Confidence not reviewed" inline
    try {
      await api.post(`/cases/${row.record_id}/confidence/confirm`);
      toast.success(`Confidence confirmed · ${row.case_id}`);
      setData((d) => ({ ...d, needs_attention: (d.needs_attention || []).map((r) => r.record_id === row.record_id ? { ...r, reasons: (r.reasons || []).filter((x) => !x.startsWith("Confidence not reviewed")) } : r).filter((r) => (r.reasons || []).length > 0) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm"); }
  };

  const completeTask = async (id) => {
    try { await api.post(`/standup-notes/${id}/done`); toast.success("Marked done"); load(); }
    catch { toast.error("Failed"); }
  };

  const markHandled = async (record_id) => {
    try { await api.post("/whatsapp/handled", { case_record_id: record_id }); toast.success("Marked handled"); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading || !data) {
    return <Layout><div className="max-w-5xl mx-auto px-6 py-8 space-y-4"><Loading label="Loading your day…" rows={6} onRetry={load} section="My Day" /></div></Layout>;
  }
  if (isPreparing(data)) return <Layout><Preparing section="My Day" onRetry={load} /></Layout>;
  const c = data.counts;
  const dismiss = async (kind, key, case_record_id, until) => {
    try {
      await api.post("/suggestions/dismiss", { kind, key, case_record_id, until: until || null });
      toast.success(until ? "We'll remind you later" : "Hidden", { action: { label: "Undo", onClick: async () => { await api.post("/suggestions/undismiss", { kind, key }); load(); } }, duration: 6000 });
      load();
    } catch { toast.error("Failed"); }
  };
  const promiseAct = async (p, what) => {
    try {
      const r = await api.post(`/cases/${p.case_record_id}/whatsapp/promises/${p.idx}/${what}`);
      if (what === "toggle") toast.success("Marked done");
      else toast.success("Removed — won't come back", { action: { label: "Undo", onClick: async () => { await api.post(`/cases/${p.case_record_id}/whatsapp/promises/undismiss`, { what: r.data.what, by_when: r.data.by_when }); load(); } }, duration: 6000 });
      load();
    } catch { toast.error("Failed"); }
  };
  const uid = user?.email || "me";
  return (
    <Layout>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><Sun className="w-5 h-5 text-[#FF4802]" /><h1 className="text-2xl font-semibold tracking-tight text-[#333333]">My Day</h1></div>
        <NewLeadButton />
      </div>
      <p className="text-sm text-[#6B7280] mb-5" data-testid="myday-counts">
        {data.section_errors?.length > 0 && <span className="block text-xs text-[#92600a] mb-1" data-testid="myday-section-errors">Some sections couldn't load ({data.section_errors.join(", ")}) — the rest of My Day is up to date.</span>}
        <b className="text-[#CA1212]">{c.overdue} overdue</b> · <b>{c.today} today</b> · <b className="text-[#92600a]">{c.need_attention} need attention</b>
        {data._cached_at && <span className="ml-2 text-xs text-[#C5C4C4]" data-testid="myday-updated">· updated {Math.max(0, Math.round((Date.now() - new Date(data._cached_at).getTime()) / 60000))} min ago</span>}
      </p>

      {data.brief && (
        <div className="mb-5 rounded-2xl border border-[#222222] bg-[#222222] text-white p-4" data-testid="myday-brief">
          <div className="text-sm font-medium mb-2">Morning brief <span className="text-[11px] text-[#C5C4C4] font-normal">· what needs you today</span></div>
          {data.brief.empty ? <div className="text-xs text-[#C5C4C4]" data-testid="brief-empty">Nothing urgent this morning — start with today's follow-ups or open <button onClick={() => navigate("/cases")} className="text-[#FF6F38] hover:underline">your cases</button>.</div> : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            {[["stuck", "Stuck cases"], ["quiet", "Gone quiet"], ["promises", "Promises due today"], ["follow_ups", "Follow-ups due today"]].map(([k, l]) => (
              <div key={k} data-testid={`brief-${k}`}><div className="text-[#C5C4C4] uppercase text-[10px] mb-1">{l} ({data.brief[k].length})</div>
                {data.brief[k].length === 0 ? <div className="text-[#6B7280]">—</div> : data.brief[k].map((x) => <div key={x.record_id} onClick={() => navigate(`/cases/${x.record_id}`)} className="cursor-pointer hover:text-[#FF6F38] py-0.5"><span className="font-mono text-[#FF6F38] font-semibold">{x.case_id}</span> · {x.line}</div>)}</div>))}
          </div>)}
        </div>
      )}

      <MyConsultStrip />
      <GmailBanner gmail={data.gmail} onChanged={load} />
      <EmailToAssign items={data.email_to_assign || []} onDone={load} />
      <LeakingSection cases={data.consult_leaking || []} navigate={navigate} onDone={load} uid={uid} />
      <CheckinSection cases={data.consult_followups || []} navigate={navigate} onDone={load} uid={uid} onLater={(cs) => dismiss("checkin", cs.record_id, cs.record_id, new Date(Date.now() + 86400000).toISOString())} />
      <CatchupSection cases={wa.catchup} navigate={navigate} onLinked={load} uid={uid} onSkip={(cs) => dismiss("catchup", cs.record_id, cs.record_id)} />
      <Section title="Overdue follow-ups" count={data.overdue.length} icon={AlertTriangle} tone="#CA1212" testid="myday-overdue" uid={uid} defaultOpen>
        <CaseList rows={data.overdue} navigate={navigate} empty="Nothing overdue. Nice — check today's follow-ups next." />
      </Section>
      <Section title="Today's follow-ups" count={data.today.length} icon={CalendarClock} tone="#FF4802" testid="myday-today" uid={uid} defaultOpen>
        <CaseList rows={data.today} navigate={navigate} empty="No follow-ups due today. Pick a case from Needs attention or log a conversation to set the next one." />
      </Section>
      {(data.esc_tasks || []).length > 0 && (
        <Section title="8D reports" count={data.esc_tasks.length} icon={AlertTriangle} tone="#CA1212" testid="myday-8d" uid={uid} defaultOpen>
          <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden">
            {data.esc_tasks.map((t) => <div key={t.id} onClick={() => navigate(`/cases/${t.lead_id}`)} data-testid={`esc-task-${t.id}`} className="flex items-center justify-between px-3.5 py-2.5 border-b border-[#F7F7F7] last:border-0 cursor-pointer hover:bg-[#F6F5EF]/70 text-sm"><span><span className="font-mono font-semibold text-[#E2521B] text-xs">{t.case_id}</span> <span className="text-[#333333] ml-2">{t.type}</span></span><span className={`text-xs font-medium ${t.overdue_8d ? "text-[#CA1212]" : "text-[#92600a]"}`}>Create 8D report · {t.line}</span></div>)}
          </div>
        </Section>
      )}
      <Section title="Stuck cases" count={(data.stuck || []).length} icon={AlertTriangle} tone="#CA1212" testid="myday-stuck" hint="stuck" uid={uid} defaultOpen={(data.stuck || []).length > 0}>
        {(data.stuck || []).length === 0 ? <p className="text-sm text-[#6B7280]">No case is over its TAT limit.</p> : <StuckList rows={data.stuck} navigate={navigate} testid="myday-stuck-list" />}
      </Section>
      {(data.nps_tasks || []).length > 0 && (
        <Section title="Customer feedback" count={data.nps_tasks.length} icon={Star} tone="#F5B71D" testid="myday-nps" hint="nps_tasks" uid={uid}>
          <div className="space-y-1">{data.nps_tasks.map((t) => <NpsTaskRow key={t.record_id} t={t} navigate={navigate} onDone={load} />)}</div>
        </Section>
      )}
      <FromPartners fp={data.from_partners} navigate={navigate} onDone={load} uid={uid} />
      {(data.ai_review || []).length > 0 && (
        <Section title="Review AI updates" count={data.ai_review.length} icon={Sparkles} tone="#8B46FF" testid="myday-ai-review" hint="ai_review" uid={uid}>
          <div className="space-y-1">
            {data.ai_review.map((a) => <div key={a.record_id} onClick={() => navigate(`/cases/${a.record_id}`)} data-testid={`myday-ai-${a.case_id}`} className="flex items-center gap-2 text-xs bg-white border border-[#8B46FF]/30 rounded-lg px-3 py-1.5 cursor-pointer hover:border-[#8B46FF]"><span className="font-mono text-[#E2521B] font-semibold">{a.case_id}</span><span className="text-[#333333]">{plural(a.pending, "suggested update")} from the call</span><span className="text-[#6B7280]">· {a.source_label}</span></div>)}
          </div>
        </Section>
      )}
      <Section title="Needs attention" count={data.needs_attention.length + data.partner_updates.length + data.my_tasks.length + (data.action_needed || []).length} icon={AlertTriangle} tone="#92600a" testid="myday-needs" hint="myday_needs" uid={uid} defaultOpen>
        {(data.action_needed || []).length > 0 && <div className="mb-2"><ActionNeeded items={data.action_needed} onChanged={load} showCase /></div>}
        {data.partner_updates.length > 0 && (
          <div className="mb-2 space-y-1">
            {data.partner_updates.map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-xs bg-[#F5B71D]/10 border border-[#F5B71D]/30 rounded-lg px-3 py-1.5" data-testid={`myday-partner-${u.case_id}`}>
                <Megaphone className="w-3.5 h-3.5 text-[#92600a]" /><span className="font-mono text-[#E2521B]">{u.case_id}</span>
                <span className="text-[#6B7280]">{u.spoc_name}: {u.text}</span>
              </div>
            ))}
          </div>
        )}
        {data.my_tasks.length > 0 && (
          <div className="mb-2 space-y-1">
            {data.my_tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-xs bg-[#8B46FF]/5 border border-[#8B46FF]/20 rounded-lg px-3 py-1.5" data-testid={`myday-task-${t.id}`}>
                <span><CheckSquare className="w-3.5 h-3.5 text-[#8B46FF] inline mr-1" /><span className="font-mono text-[#E2521B]">{t.case_id}</span> <span className="text-[#333333]">{t.text}</span>{t.due_date ? <span className="text-[#C5C4C4]"> · due {t.due_date}</span> : ""}</span>
                <button onClick={() => completeTask(t.id)} data-testid={`task-done-${t.id}`} className="text-[#38A2FF] hover:underline">Done</button>
              </div>
            ))}
          </div>
        )}
        <CaseList rows={data.needs_attention} navigate={navigate} empty="All clear — every active case has a next action, profile and confidence." showReasons onConfirmConfidence={confirmConfidence} />
      </Section>
      <Section title="Promises to customers" count={(data.wa_promises || []).length} icon={MessageCircle} tone="#159A50" testid="myday-wa-promises" hint="wa_promises" uid={uid} defaultOpen={(data.wa_promises || []).length > 0}>
        {(data.wa_promises || []).length === 0 ? <p className="text-sm text-[#6B7280]">No open promises from WhatsApp.</p> : (
          <div className="space-y-1">
            {data.wa_promises.map((p) => (
              <div key={`${p.case_record_id}-${p.idx}`} data-testid={`wa-promise-row-${p.case_id}`} className="flex items-center gap-2 text-xs bg-[#159A50]/5 border border-[#159A50]/20 rounded-lg px-3 py-1.5">
                <MessageCircle className="w-3.5 h-3.5 text-[#159A50]" />
                <CaseLink recordId={p.case_record_id} className="font-mono text-[#E2521B] hover:underline">{p.case_id}</CaseLink>
                <span className="text-[#333333] flex-1">Promised: {p.what}{p.by_when ? ` · ${p.by_when}` : ""}</span>
                <button onClick={() => promiseAct(p, "toggle")} data-testid={`wa-promise-done-${p.case_id}-${p.idx}`} className="text-[#159A50] hover:underline">Done</button>
                <button onClick={() => promiseAct(p, "dismiss")} data-testid={`wa-promise-dismiss-${p.case_id}-${p.idx}`} className="text-[#6B7280] hover:underline">Not a promise</button>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section title="Customers waiting for a reply" count={(data.wa_waiting || []).length} icon={MessageCircle} tone="#159A50" testid="myday-wa-waiting" hint="myday_waiting" uid={uid} defaultOpen>
        {(data.wa_waiting || []).length === 0 ? <p className="text-sm text-[#6B7280]">No customers waiting — every linked chat has been answered.</p> : (
          <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden">
            {data.wa_waiting.map((r) => (
              <div key={r.record_id} data-testid={`wa-wait-row-${r.case_id}`} className="flex items-center justify-between px-3.5 py-2.5 border-b border-[#F7F7F7] last:border-0 hover:bg-[#F6F5EF]/70">
                <div className="min-w-0 cursor-pointer" onClick={() => navigate(`/cases/${r.record_id}`)}>
                  <CaseLink recordId={r.record_id} className="font-mono font-semibold text-[#E2521B] text-xs hover:underline">{r.case_id}</CaseLink>
                  <span className="text-sm text-[#333333] ml-2">{r.borrower || r.name}</span>
                  <div className="text-xs text-[#6B7280] truncate max-w-[520px]">"{r.waiting?.last_message}"</div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {r.waiting?.criticality != null && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#CA1212]/10 text-[#CA1212]">crit {r.waiting.criticality}</span>}
                  <span className="flex items-center gap-1 text-[#CA1212] font-medium" data-testid={`wa-wait-dur-${r.case_id}`}><Clock className="w-3 h-3" />{r.waiting?.human || `${r.waiting?.hours}h`}</span>
                  <button onClick={() => navigate(`/cases/${r.record_id}`)} data-testid={`wa-wait-open-${r.case_id}`} className="text-[#159A50] hover:underline">Open chat</button>
                  <button onClick={() => markHandled(r.record_id)} data-testid={`wa-wait-handled-${r.case_id}`} className="text-[#6B7280] hover:underline">Mark handled</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {(data.wa_quiet || []).length > 0 && (
        <Section title="Gone quiet" count={data.wa_quiet.length} icon={MessageCircle} tone="#6B7280" testid="myday-wa-quiet" uid={uid}>
          <p className="text-xs text-[#6B7280] mb-2">Customer messages unanswered for more than 7 days.</p>
          <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden">
            {data.wa_quiet.map((r) => (
              <div key={r.record_id} data-testid={`wa-quiet-row-${r.case_id}`} className="flex items-center justify-between px-3.5 py-2.5 border-b border-[#F7F7F7] last:border-0 hover:bg-[#F6F5EF]/70">
                <div className="min-w-0 cursor-pointer" onClick={() => navigate(`/cases/${r.record_id}`)}>
                  <CaseLink recordId={r.record_id} className="font-mono font-semibold text-[#E2521B] text-xs hover:underline">{r.case_id}</CaseLink>
                  <span className="text-sm text-[#333333] ml-2">{r.borrower || r.name}</span>
                  <div className="text-xs text-[#6B7280] truncate max-w-[520px]">"{r.waiting?.last_message}"</div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1 text-[#6B7280]"><Clock className="w-3 h-3" />{r.waiting?.human}</span>
                  <button onClick={() => navigate(`/cases/${r.record_id}`)} className="text-[#159A50] hover:underline">Open</button>
                  <button onClick={() => markHandled(r.record_id)} data-testid={`wa-quiet-handled-${r.case_id}`} className="text-[#6B7280] hover:underline">Mark handled</button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <NudgesCard nudges={wa.nudges} navigate={navigate} onChanged={load} onSkip={(n) => dismiss("nudge", n.chat_id, n.case?.record_id)} />
    </Layout>
  );
}

function NudgesCard({ nudges, navigate, onChanged, onSkip }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  if (!nudges?.length) return null;
  const act = async (n, chat_type) => {
    try {
      if (chat_type === "hide") { await api.post(`/whatsapp/chats/hide`, { chat_id: n.chat_id }); toast.success("Hidden"); }
      else { const r = await api.post(`/cases/${n.case.record_id}/whatsapp/link`, { chat_id: n.chat_id, chat_type }); toast.success(`Linked to ${n.case.case_id}${r.data.importing ? " · importing history in the background" : ""}`); }
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rows = all ? nudges : nudges.slice(0, 5);
  const btn = (p) => `text-[11px] rounded-lg px-2 py-1 font-medium whitespace-nowrap ${p ? "bg-[#FF4802] text-white" : "border border-[#E2E2E2] text-[#6B7280] hover:border-[#FF4802]"}`;
  return (
    <div className="mb-5 rounded-2xl border border-[#38A2FF]/30 bg-[#38A2FF]/5 p-4" data-testid="myday-wa-nudges">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#333333] font-medium flex items-center gap-2"><Link2 className="w-4 h-4 text-[#38A2FF]" /> {plural(nudges.length, "new WhatsApp chat")} may belong to your cases</div>
        <button onClick={() => setOpen((o) => !o)} data-testid="wa-nudges-review" className="text-xs bg-[#38A2FF] text-white rounded-lg px-3 py-1.5 font-medium">{open ? "Hide" : "Review"}</button>
      </div>
      {open && (
        <div className="mt-3">
          <p className="text-xs text-[#6B7280] mb-2">Chats on the team's WhatsApp numbers that look like your customers'. Link them so messages show on the case.</p>
          <div className="rounded-xl border border-[#E2E2E2] bg-white overflow-hidden">
            {rows.map((n) => (
              <div key={n.chat_id} className="px-3.5 py-2.5 border-b border-[#F7F7F7] last:border-0 text-sm" data-testid={`wa-nudge-${n.chat_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[#333333] truncate font-medium">{n.name || n.chat_id} <span className="text-[11px] text-[#C5C4C4] font-normal">· {n.type_label}</span></div>
                    <div className="text-[11px] text-[#6B7280] truncate">→ <button onClick={() => navigate(`/cases/${n.case.record_id}`)} className="font-mono text-[#E2521B] font-semibold hover:underline">{n.case.case_id}</button> {n.case.borrower} · {(n.reasons || []).join(", ")}</div>
                    {n.preview?.text && <div className="text-[11px] text-[#C5C4C4] truncate">"{n.preview.text}"</div>}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {n.kind === "1:1" ? <button onClick={() => act(n, "customer")} data-testid={`wa-nudge-customer-${n.chat_id}`} className={btn(false)}>Link 1:1 (customer prefers 1:1)</button>
                      : <button onClick={() => act(n, "customer")} data-testid={`wa-nudge-customer-${n.chat_id}`} className={btn(n.likely_type !== "partner")}>Link as customer group</button>}
                    {n.kind !== "1:1" && <button onClick={() => act(n, "partner")} data-testid={`wa-nudge-partner-${n.chat_id}`} className={btn(n.likely_type === "partner")}>Link as partner group</button>}
                    <button onClick={() => onSkip(n)} data-testid={`wa-nudge-skip-${n.chat_id}`} className="text-[11px] text-[#6B7280] hover:underline whitespace-nowrap">Not this case</button>
                    <button onClick={() => act(n, "hide")} data-testid={`wa-nudge-hide-${n.chat_id}`} className="text-[11px] text-[#6B7280] hover:underline whitespace-nowrap">Not a loan chat</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {nudges.length > 5 && !all && <button onClick={() => setAll(true)} data-testid="wa-nudges-see-all" className="text-xs text-[#38A2FF] hover:underline mt-2">See all {nudges.length}</button>}
        </div>
      )}
    </div>
  );
}

function FromPartners({ fp, navigate, onDone, uid }) {
  if (!fp) return null;
  const n = (fp.uploads?.length || 0) + (fp.queries?.length || 0) + (fp.updates?.length || 0);
  if (n === 0) return null;
  const act = async (url, msg) => { try { await api.post(url); toast.success(msg); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const Row = ({ c, children, testid, red }) => (
    <div className={`flex flex-wrap items-center gap-2 text-xs rounded-lg px-3 py-1.5 border ${red ? "bg-[#CA1212]/5 border-[#CA1212]/40" : "bg-white border-[#E2E2E2]"}`} data-testid={testid}>
      <CaseLink recordId={c.case_record_id} className="font-mono text-[#E2521B] font-semibold hover:underline">{c.case_id}</CaseLink>{children}
    </div>
  );
  return (
    <Section title="From partners" count={n} icon={Megaphone} tone="#38A2FF" testid="myday-from-partners" hint="from_partners" uid={uid} defaultOpen>
      <div className="space-y-1">
        {fp.queries.map((q) => <Row key={q.id} c={q} red={q.overdue} testid={`myday-pquery-${q.case_id}`}>
          <span className="text-[#333333]">Bank query · {q.text}</span>{q.documents?.length > 0 && <span className="text-[#6B7280]">({q.documents.join(", ")})</span>}
          <span className="text-[#6B7280]">{q.company} · {fmtLastConv(q.created_at)}{q.overdue && <span className="text-[#CA1212] font-medium"> · unanswered 24h+</span>}</span>
          <button onClick={() => act(`/partner-queries/${q.id}/shared`, "Marked as shared with partner")} data-testid={`myday-pquery-shared-${q.id}`} className="ml-auto px-2 py-0.5 rounded-full border border-[#159A50]/40 text-[#159A50] hover:bg-[#159A50]/5">Shared with partner</button>
        </Row>)}
        {fp.uploads.map((u) => <Row key={u.id} c={u} testid={`myday-pupload-${u.case_id}`}>
          <span className="text-[#333333]">New from partner · {u.company} uploaded: {u.document_type}</span><span className="text-[#6B7280]">{fmtLastConv(u.created_at)}</span>
          <button onClick={() => act(`/partner-uploads/${u.id}/seen`, "Marked seen")} data-testid={`myday-pupload-seen-${u.id}`} className="ml-auto px-2 py-0.5 rounded-full border border-[#E2E2E2] text-[#6B7280] hover:border-[#FF4802]">Seen</button>
        </Row>)}
        {fp.updates.map((u) => <Row key={u.id} c={u} testid={`myday-pupdate-${u.case_id}`}>
          <span className="text-[#333333]">{u.spoc_name}: {u.text}</span>{u.suggested_stage && <span className="px-2 py-0.5 rounded-full bg-[#8B46FF]/10 text-[#8B46FF]">suggests {u.suggested_stage}</span>}
          <span className="text-[#6B7280]">{fmtLastConv(u.created_at)}</span>
        </Row>)}
      </div>
    </Section>
  );
}

function LeakingSection({ cases, navigate, onDone, uid }) {
  const [lostReasons, setLostReasons] = useState([]);
  useEffect(() => { loadMeta().then((m) => setLostReasons(m?.selects?.[FIELDS.LOST_REASON] || [])).catch(() => {}); }, []);
  if (!cases || cases.length === 0) return null;
  return (
    <Section title="Consulted leads without a follow-up" count={cases.length} icon={AlertTriangle} tone="#CA1212" testid="myday-leaking" hint="consult_leaking" uid={uid} defaultOpen>
      <div className="rounded-2xl border border-[#CA1212]/40 bg-white shadow-sm overflow-hidden">
        {cases.map((cs) => (
          <div key={cs.record_id} className="border-b border-[#F7F7F7] last:border-0 px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid={`leak-row-${cs.case_id}`}>
            <div className="min-w-0 cursor-pointer" onClick={() => navigate(`/cases/${cs.record_id}`)}>
              <span className="font-mono font-semibold text-[#E2521B] text-xs">{cs.case_id}</span>
              <span className="text-sm text-[#333333] ml-2">{cs.borrower || cs.name}</span>
              <span className="text-[11px] text-[#CA1212] ml-2 font-medium">{cs.consult?.leak_reason}</span>
              <span className="text-[11px] text-[#6B7280] ml-2">{cs.purchase_status || "—"} · last conv {fmtLastConv(cs.last_conversation)}</span>
            </div>
            <LeakRowActions cs={cs} lostReasons={lostReasons} onDone={onDone} />
          </div>
        ))}
      </div>
    </Section>
  );
}

function CheckinSection({ cases, navigate, onDone, uid, onLater }) {
  if (!cases || cases.length === 0) return null;
  return (
    <Section title="Post-consultation check-in" count={cases.length} icon={CalendarClock} tone="#8B46FF" testid="myday-consult" hint="myday_checkin" uid={uid} defaultOpen>
      <div className="space-y-2">
        {cases.map((cs) => (
          <div key={cs.record_id}>
            <CheckinCard cs={cs} onDone={onDone} />
            <button onClick={() => onLater(cs)} data-testid={`checkin-later-${cs.case_id}`} className="text-[11px] text-[#6B7280] hover:underline mt-1 ml-1">Remind me tomorrow</button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function CatchupSection({ cases, navigate, onLinked, uid, onSkip }) {
  const [open, setOpen] = useState(null);
  if (!cases || cases.length === 0) return null;
  const link = async (record_id, chat_id, chat_type) => {
    try {
      const r = await api.post(`/cases/${record_id}/whatsapp/link`, { chat_id, chat_type });
      toast.success(`Linked${r.data.importing ? " · importing history in the background" : ""}`);
      onLinked();
    } catch { toast.error("Link failed"); }
  };
  const btnCls = (primary) => `text-xs rounded-lg px-2.5 py-1.5 font-medium whitespace-nowrap ${primary ? "bg-[#FF4802] text-white" : "border border-[#E2E2E2] text-[#6B7280] hover:border-[#FF4802]"}`;
  return (
    <Section title="Link your existing WhatsApp groups" count={cases.length} icon={MessageCircle} tone="#8B46FF" testid="myday-wa-catchup" hint="myday_catchup" uid={uid}>
      <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden">
        {cases.map((cs) => (
          <div key={cs.record_id} className="border-b border-[#F7F7F7] last:border-0">
            <button onClick={() => setOpen(open === cs.record_id ? null : cs.record_id)} data-testid={`wa-catchup-${cs.case_id}`} className="w-full flex items-center justify-between px-3.5 py-2.5 text-sm hover:bg-[#F6F5EF]/70 text-left">
              <span>
                <span className="font-mono font-semibold text-[#E2521B] text-xs">{cs.case_id}</span>
                <span className="text-[#333333] ml-2">{cs.borrower || cs.name}</span>
              </span>
              <span className="text-[11px] text-[#6B7280]">{(cs.suggestions || []).length} suggested {open === cs.record_id ? "▲" : "▼"}</span>
            </button>
            {open === cs.record_id && (
              <div className="px-3.5 pb-2.5 space-y-1.5">
                {(cs.suggestions || []).map((s) => (
                  <div key={s.chat_id} data-testid={`wa-catchup-sugg-${s.chat_id}`} className="flex items-center justify-between gap-3 text-xs bg-[#F7F7F7] rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-[#333333] truncate font-medium">{s.name || s.chat_id} <span className="text-[11px] text-[#C5C4C4] font-normal">· {s.type_label}</span></div>
                      <div className="text-[11px] text-[#6B7280]">{(s.reasons || []).join(" · ") || "possible match"}{s.preview?.text ? <span className="text-[#C5C4C4]"> · "{s.preview.text}"</span> : null}</div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      {s.kind === "1:1" ? <button onClick={() => link(cs.record_id, s.chat_id, "customer")} data-testid={`wa-catchup-customer-${s.chat_id}`} className={btnCls(false)}>Link 1:1 (customer prefers 1:1)</button>
                        : <><button onClick={() => link(cs.record_id, s.chat_id, "customer")} data-testid={`wa-catchup-customer-${s.chat_id}`} className={btnCls(s.likely_type === "customer")}>Link as customer group</button>
                        <button onClick={() => link(cs.record_id, s.chat_id, "partner")} data-testid={`wa-catchup-partner-${s.chat_id}`} className={btnCls(s.likely_type === "partner")}>Link as partner group</button></>}
                    </div>
                  </div>
                ))}
                <button onClick={() => onSkip(cs)} data-testid={`wa-catchup-none-${cs.case_id}`} className="text-[11px] text-[#6B7280] hover:underline">None of these — skip this case</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function Section({ title, count, icon: Icon, tone, children, testid, action, hint, uid, defaultOpen = false }) {
  const key = `myday_open:${uid}:${testid}`;
  const [open, setOpen] = useState(() => { const v = localStorage.getItem(key); return v === null ? defaultOpen : v === "1"; });
  const toggle = () => { setOpen((o) => { localStorage.setItem(key, o ? "0" : "1"); return !o; }); };
  return (
    <div className="mb-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <button onClick={toggle} data-testid={`${testid}-toggle`} aria-expanded={open} className="text-sm font-medium flex items-center gap-1.5 text-left" style={{ color: tone || "#333333" }}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {Icon && <Icon className="w-4 h-4" />} {title}{count != null && <span className="text-xs font-normal opacity-70" data-testid={`${testid}-count`}>({count})</span>}
          </button>
          {hint && <Info k={hint} />}
        </div>
        {action}
      </div>
      {open && children}
    </div>
  );
}

function CaseList({ rows, navigate, empty, showReasons, showNotes, onConfirmConfidence }) {
  const [byProp, setByProp] = useState(() => localStorage.getItem("myday_group_property") === "1");
  if (!rows || rows.length === 0) return <p className="text-sm text-[#6B7280]">{empty}</p>;
  const flip = () => { localStorage.setItem("myday_group_property", byProp ? "0" : "1"); setByProp(!byProp); };
  const groups = byProp ? ["Finalised", "Not finalised", "Unknown"].map((k) => ({ k, rows: rows.filter((c) => (c.property_bucket || "Unknown") === k) })).filter((g) => g.rows.length) : [{ k: null, rows }];
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden">
      <div className="flex justify-end px-3 pt-2"><button onClick={flip} data-testid="myday-group-property" className={`text-[11px] px-2 py-0.5 rounded-full border ${byProp ? "bg-[#FF4802] text-white border-[#FF4802]" : "text-[#6B7280] border-[#E2E2E2]"}`}>Group by property</button></div>
      {groups.map((g) => (
        <div key={g.k || "all"}>
          {g.k && <div className="px-3.5 py-1.5 text-[11px] font-medium text-[#6B7280] bg-[#F6F5EF]" data-testid={`myday-prop-group-${g.k.replace(/\s+/g, "-").toLowerCase()}`}>Property {g.k.toLowerCase()} · {plural(g.rows.length, "case")} · {formatINR(g.rows.reduce((s, c) => s + (Number(c.loan_amount) || 0), 0))}</div>}
          <CaseTable rows={g.rows} navigate={navigate} showReasons={showReasons} showNotes={showNotes} onConfirmConfidence={onConfirmConfidence} />
        </div>
      ))}
    </div>
  );
}

function CaseTable({ rows, navigate, showReasons, showNotes, onConfirmConfidence }) {
  return (
    <div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[11px] uppercase text-[#6B7280] border-b border-[#E2E2E2] tracking-wider">
            <th className="py-2 px-3.5 font-medium">Case</th>
            <th className="py-2 px-3.5 font-medium">Borrower</th>
            <th className="py-2 px-3.5 font-medium">Stage · days</th>
            <th className="py-2 px-3.5 font-medium">Next action</th>
            <th className="py-2 px-3.5 font-medium">Follow-up</th>
            <th className="py-2 px-3.5 font-medium">Confidence</th>
            {showNotes && <th className="py-2 px-3.5 font-medium">Notes</th>}
            {showReasons && <th className="py-2 px-3.5 font-medium">Why</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.record_id} onClick={() => navigate(`/cases/${c.record_id}`)} data-testid={`myday-row-${c.case_id}`} className="border-b border-[#F7F7F7] last:border-0 hover:bg-[#F6F5EF]/70 cursor-pointer">
              <td className="py-2.5 px-3.5 whitespace-nowrap"><CaseLink recordId={c.record_id} className="font-mono font-semibold text-[#E2521B] hover:underline">{c.case_id}</CaseLink></td>
              <td className="py-2.5 px-3.5 font-medium text-[#333333]">{c.borrower || c.name || "—"}</td>
              <td className="py-2.5 px-3.5"><StageChip stage={c.stage} />{c.days_in_stage != null && <span className="text-[#C5C4C4] ml-1">{c.days_in_stage}d</span>}</td>
              <td className="py-2.5 px-3.5 text-[#6B7280] max-w-[200px] truncate">{c.next_action || "—"}</td>
              <td className={`py-2.5 px-3.5 whitespace-nowrap ${c.follow_up_date && c.follow_up_date.slice(0, 10) < new Date().toISOString().slice(0, 10) ? "text-[#CA1212] font-semibold" : "text-[#6B7280]"}`}>{fmtDate(c.follow_up_date)}</td>
              <td className="py-2.5 px-3.5 text-[#6B7280]">{c.rm_confidence || "—"}</td>
              {showNotes && (
                <td className="py-2.5 px-3.5">
                  {c.consultation_notes_link
                    ? <a href={c.consultation_notes_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`myday-notes-${c.case_id}`} className="text-[#38A2FF] hover:underline">Notes</a>
                    : <span className="text-[#C5C4C4]">—</span>}
                </td>
              )}
              {showReasons && (
                <td className="py-2.5 px-3.5 text-[#CA1212]">
                  {(c.reasons || []).map((r, i) => (
                    <span key={r} className="inline-flex items-center gap-1">
                      {i > 0 && <span>, </span>}{r}
                      {r.startsWith("Confidence not reviewed") && onConfirmConfidence && (
                        <button onClick={(e) => { e.stopPropagation(); onConfirmConfidence(c); }} data-testid={`myday-confirm-confidence-${c.case_id}`}
                          className="ml-1 px-2 py-0.5 rounded-full text-[11px] border border-[#159A50]/40 text-[#159A50] bg-white hover:bg-[#159A50]/5">Confirm</button>
                      )}
                    </span>
                  ))}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
